import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ConfirmDialog from './ConfirmDialog';
import CardGrid from './CardGrid';
import type { DragHandleProps } from './SortableBlock';
import type { NavNode } from '../../types/nav';
import type { CardMetadata } from '../../types/card';
import type { AddCardFn, AddCardExtra, SetThumbnailFn } from '../../types/addCard';

interface Props {
  block: NavNode;
  pages: NavNode[];
  cards: CardMetadata[];
  onAddCard?: AddCardFn;
  onUpdateCard?: (id: string, updates: { title: string; summary: string; url: string }) => Promise<void>;
  onSetThumbnail?: SetThumbnailFn;
  onDeleteCard?: (id: string) => Promise<void>;
  onUpdate: (blockId: string, title: string) => Promise<void>;
  onDelete: (blockId: string) => Promise<void>;
  dragHandleProps?: DragHandleProps;
  containerId?: string;
  sortableCardIds?: string[];
  isDragActive?: boolean;
}

export default function CardGroupBlock({
  block,
  pages,
  cards,
  onAddCard,
  onUpdateCard,
  onSetThumbnail,
  onDeleteCard,
  onUpdate,
  onDelete,
  dragHandleProps,
  containerId,
  sortableCardIds,
  isDragActive,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(block.title);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleStartEdit = () => {
    setDraft(block.title);
    setEditing(true);
  };

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== block.title) {
      await onUpdate(block.id, trimmed);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(block.title);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleDeleteConfirm = async () => {
    setConfirmOpen(false);
    await onDelete(block.id);
  };

  // Filter cards that belong to this block's pages
  const blockPageIds = new Set(pages.map((p) => p.cardId).filter(Boolean));
  const blockCards = cards.filter((c) => blockPageIds.has(c.id));

  // Wrap onAddCard to inject this block's id while forwarding upload extras
  const handleAddCard: AddCardFn | undefined = onAddCard
    ? (title: string, url: string, summary: string, _blockId?: string, extra?: AddCardExtra) =>
        onAddCard(title, url, summary, block.id, extra)
    : undefined;

  return (
    <Box
      sx={{
        borderLeft: '3px solid #E5E7EB',
        pl: 2,
        mb: 3,
        '&:hover .group-action-btn': { opacity: 1 },
        '&:hover .block-drag-handle': { opacity: 1 },
      }}
    >
      {/* Group header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          position: 'relative',
        }}
      >
        {dragHandleProps && (
          <Box
            ref={dragHandleProps.ref}
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
            className="block-drag-handle"
            sx={{
              display: 'flex',
              cursor: 'grab',
              color: 'text.disabled',
              flexShrink: 0,
              opacity: 0,
              transition: 'opacity 150ms ease-in-out',
              ml: -4.5,
            }}
            aria-label={`Reorder ${block.title}`}
          >
            <DragIndicatorIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        {editing ? (
          <TextField
            inputRef={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            variant="standard"
            fullWidth
            slotProps={{
              input: {
                sx: {
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#111827',
                },
              },
            }}
          />
        ) : (
          <Typography
            onClick={handleStartEdit}
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: '#111827',
              cursor: 'text',
              flex: 1,
            }}
          >
            {block.title}
          </Typography>
        )}

        {!editing && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              className="group-action-btn"
              size="small"
              onClick={handleStartEdit}
              aria-label="Rename group"
              sx={{
                opacity: 0,
                transition: 'opacity 150ms ease-in-out',
                color: 'text.disabled',
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              className="group-action-btn"
              size="small"
              onClick={() => setConfirmOpen(true)}
              aria-label="Delete group"
              sx={{
                opacity: 0,
                transition: 'opacity 150ms ease-in-out',
                color: 'text.disabled',
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Card grid for this group */}
      <CardGrid
        cards={blockCards}
        loading={false}
        hasSection
        onAddCard={handleAddCard}
        onUpdateCard={onUpdateCard}
        onSetThumbnail={onSetThumbnail}
        onDeleteCard={onDeleteCard}
        containerId={containerId}
        sortableCardIds={sortableCardIds}
        isDragActive={isDragActive}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete this group?"
        message="Cards in this group will be moved to ungrouped."
        confirmLabel="Delete Group"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
